import React from "react";
import { cn } from "../../../common/u1/cn";
import { SearchField } from "../../../common/u1/SearchField";
import { Pagination } from "../../../common/u1/Pagination";
import { TrackedBusinessesTable } from "./TrackedBusinessesTable";
import { TrackedBusinessCardList } from "./TrackedBusinessCardList";
import { useIsWide } from "../../../common/u1/useIsWide";

/* The tracked-businesses card: its count, its search, its table and its pager. */
export const TrackedBusinessesCard = ({
  rows = [],
  total = 0,
  page = 1,
  totalPages = 1,
  pageSize,
  query = "",
  onQueryChange = () => {},
  onPageChange = () => {},
  onOpenProfile,
  onOpenChanges,
  onCloseChanges,
  className = "",
}) => {
  /* The table's five columns need 820px before they fit, so the cards take
     over below `md`. NOT `lg`: this project's scale is sm 576 / md 960 /
     lg 1440, so switching at lg would hand a 1200px laptop the phone layout. */
  const isWide = useIsWide("(min-width: 960px)");

  /* One message for both layouts: a search that matches nothing says so, and
     an empty list says something else. */
  const emptyMessage = query.trim()
    ? "No businesses match that search."
    : "No businesses to show.";

  return (
  <section
    aria-labelledby="u1-tracked-heading"
    className={cn(
      "overflow-hidden rounded-[18px] border border-[var(--line)] bg-white",
      "shadow-[0_1px_2px_rgba(20,40,60,.03)]",
      className
    )}
  >
    <div className="flex flex-wrap items-start justify-between gap-3 p-5 sm:px-[26px] sm:pt-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h3
            id="u1-tracked-heading"
            className="m-0 text-[19px] font-bold text-[var(--ink)] font-sans"
          >
            Tracking businesses
          </h3>
          {/* The live screen's own count pill, restyled. */}
          <span className="inline-flex items-center rounded-full bg-[var(--coral-soft)] px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[.06em] text-[var(--coral-deep)] font-sans">
            {total} {total === 1 ? "business" : "businesses"}
          </span>
        </div>
        <p className="mt-1.5 mb-0 text-[13.5px] text-[var(--muted)] font-sans">
          Every buyer you invited, wherever they trade from.
        </p>
      </div>

      <SearchField
        value={query}
        onChange={onQueryChange}
        placeholder="Search business"
        label="Search the businesses you track"
        className="shrink-0 sm:w-[300px]"
      />
    </div>

    {/* Two layouts, one set of rows and one set of helpers. */}
    {isWide ? (
      <TrackedBusinessesTable
        rows={rows}
        onOpenProfile={onOpenProfile}
        onOpenChanges={onOpenChanges}
        onCloseChanges={onCloseChanges}
        emptyMessage={emptyMessage}
      />
    ) : (
      <TrackedBusinessCardList
        rows={rows}
        onOpenProfile={onOpenProfile}
        onOpenChanges={onOpenChanges}
        onCloseChanges={onCloseChanges}
        emptyMessage={emptyMessage}
      />
    )}

    {totalPages > 1 && (
      <div className="border-t border-[var(--line)] bg-[var(--bg)] px-5 py-3 sm:px-[26px]">
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          itemLabel="business"
          itemLabelPlural="businesses"
          onChange={onPageChange}
          label="Tracked businesses pages"
          className="mt-0"
        />
      </div>
    )}
  </section>
  );
};

export default TrackedBusinessesCard;
